import React, { useState, useEffect, useRef } from "react";
import ImageUploading from "react-images-uploading";
import { ModalImage } from "@/Components/Add/Listings/components/ImageComponents";
import { ImageCard } from "../ImageCard";
import imageCompression from "browser-image-compression";

// Assume this is your base URL for images from S3
const IMAGE_BASE_URL = "https://s3.amazonaws.com/flyinn-app-bucket/"; // <-- IMPORTANT: Replace with your actual S3 bucket URL or CDN

const ImageUploadingComponent = ({
  value = [], // Incoming images from parent (can be empty, or API payload)
  onChange,
  maxNumber = 20,
  errors,
  setImagesMarkedForDeletion,
}) => {
  // Internal state for images, derived from value prop
  const [internalImages, setInternalImages] = useState([]);
  const [featuredIndex, setFeaturedIndex] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [description, setDescription] = useState("");
  const [compressing, setCompressing] = useState(false);

  // --- Effect to process incoming 'value' prop for existing images ---
  useEffect(() => {
    if (value && value.length > 0) {
      const processedInitialImages = value.map((img) => {
        // Check if it's already in the react-images-uploading format (e.g., new upload)
        if (img.data_url) {
          return img;
        } else {
          // It's an existing image from the backend payload
          // Construct the full URL for display
          const fullImageUrl = img.image.startsWith("http")
            ? img.image
            : `${IMAGE_BASE_URL}${img.image}`;

          return {
            id: img.id, // Keep the backend ID
            data_url: fullImageUrl, // This is what react-images-uploading uses for display
            // You might want to retain other backend properties like 'image' path
            // to send back to the server on save/update.
            // Example: originalImageUrl: img.image,
            file: null, // No file object for existing images unless re-uploaded
            description: img.description || "", // Assuming your backend provides description
            type: img.type || "photo", // Keep type if provided
          };
        }
      });
      console.log(
        "Processed initial images for display:",
        processedInitialImages
      );
      setInternalImages(processedInitialImages);
    } else {
      // If value is empty, clear internal images (e.g., for new listing)
      setInternalImages([]);
    }
  }, [value]); // Re-run when the 'value' prop changes

  // Compression function with 1MB target (for binary file size)
  const compressImage = async (file) => {
    console.log(`Original image: ${(file.size / 1024 / 1024).toFixed(2)} MB`);
    console.log(`Original image name: ${file.name}`);

    const options = {
      maxSizeMB: 0.5, // Target 1MB for the *binary* file
      useWebWorker: true,
      maxWidthOrHeight: 1920,
      fileType: file.type,
    };

    try {
      const compressedFile = await imageCompression(file, options);
      console.log(
        `Compressed binary image: ${(compressedFile.size / 1024 / 1024).toFixed(
          2
        )} MB`
      );
      // Ensure the compressed file retains the original name if possible
      // browser-image-compression usually handles this, but we'll re-verify.
      const finalFile = new File(
        [compressedFile],
        file.name || "compressed_image.jpeg",
        {
          type: compressedFile.type,
          lastModified: compressedFile.lastModified,
        }
      );
      return finalFile;
    } catch (error) {
      console.error("Compression error:", error);
      return file; // Return original file if compression fails
    }
  };

  const handleImageUploadChange = async (newImageList) => {
    setCompressing(true);
    const processedImages = [];

    for (const imageItem of newImageList) {
      if (imageItem.file) {
        // This is a NEWLY SELECTED file
        let fileToProcess = imageItem.file;

        // Only compress if the file size is greater than 0.5MB
        if (fileToProcess.size > 0.5 * 1024 * 1024) {
          fileToProcess = await compressImage(imageItem.file);
        } else {
          console.log(
            `Image within size limit: ${(
              fileToProcess.size /
              1024 /
              1024
            ).toFixed(2)} MB`
          );
        }

        // Convert the final File/Blob back to data_url for display by react-images-uploading
        const dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(fileToProcess);
        });

        processedImages.push({
          data_url: dataUrl,
          file: fileToProcess, // This is the compressed (or original if small) File object
        });
      } else {
        // This is an EXISTING image (from 'value' prop)
        // It's already in the correct format with data_url from the useEffect
        processedImages.push(imageItem);
      }
    }

    setInternalImages(processedImages); // Update internal state
    onChange(processedImages); // Notify parent component with processed list
    setCompressing(false);
  };

  const moveImage = (fromIndex, toIndex) => {
    const updatedImages = [...internalImages]; // Use internal state
    const [movedImage] = updatedImages.splice(fromIndex, 1);
    updatedImages.splice(toIndex, 0, movedImage);
    setInternalImages(updatedImages); // Update internal state
    onChange(updatedImages); // Notify parent
    setFeaturedIndex(toIndex); // Keep track of featured if needed
  };

  const handleDelete = (index, id, file) => {
    console.log({ index, id, file });
    const updatedImages = internalImages.filter((_, i) => i !== index); // Use internal state
    setInternalImages(updatedImages); // Update internal state
    onChange(updatedImages); // Notify parent

    if (!(file?.file instanceof File) && id) {
      setImagesMarkedForDeletion((prev) => [...prev, id]);
    }
  };

  const handleStarClick = (index) => {
    if (index === 0) return;
    moveImage(index, 0);
  };

  const handleDescriptionClick = (image, index) => {
    setSelectedImage({ ...image, index });
    setDescription(image.description || "");
    setIsImageModalOpen(true);
  };

  const closeImageModal = () => {
    setIsImageModalOpen(false);
    setSelectedImage(null);
    setDescription(""); // Clear description on close
  };

  const saveDescription = () => {
    if (selectedImage) {
      const updatedImages = [...internalImages]; // Use internal state
      // Ensure we update the specific item, preserving other properties
      updatedImages[selectedImage.index] = {
        ...updatedImages[selectedImage.index], // Take existing properties
        description: description, // Update description
      };
      setInternalImages(updatedImages); // Update internal state
      onChange(updatedImages); // Notify parent
    }
    closeImageModal();
  };

  return (
    <div>
      {compressing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-lg font-medium text-gray-800">
              Optimizing images...
            </p>
            <p className="text-sm text-gray-600">
              Please wait, this might take a moment.
            </p>
          </div>
        </div>
      )}

      <ImageUploading
        multiple
        value={internalImages} // Use internal state here
        onChange={handleImageUploadChange}
        maxNumber={maxNumber}
        dataURLKey="data_url"
        acceptType={["jpg", "jpeg", "png", "webp"]}
        beforeUpload={(files) => {
          const validTypes = [
            "image/jpeg",
            "image/jpg",
            "image/png",
            "image/webp",
          ];

          const invalidFiles = files.filter(
            (file) => !validTypes.includes(file.type)
          );

          if (invalidFiles.length > 0) {
            alert("Only JPG, PNG, and WEBP images are allowed.");
            return false;
          }

          const MAX_INITIAL_FILE_SIZE = 15 * 1024 * 1024;
          const tooLargeFiles = files.filter(
            (file) => file.size > MAX_INITIAL_FILE_SIZE
          );
          if (tooLargeFiles.length > 0) {
            alert(
              `Some files are too large (>${
                MAX_INITIAL_FILE_SIZE / 1024 / 1024
              }MB) and cannot be processed.`
            );
            return false;
          }

          return true;
        }}
      >
        {({ imageList, onImageUpload, isDragging, dragProps }) => {
          // imageList here will be 'internalImages' due to 'value={internalImages}'
          return (
            <div className="upload__image-wrapper">
              <div
                className="fu-text"
                style={isDragging ? { color: "red" } : null}
                onClick={onImageUpload}
                {...dragProps}
              >
                <div className="add-list-media-wrap">
                  <div className="">
                    <div className="fuzone">
                      <div className="fu-text">
                        <span>
                          <i className="fal fa-images" />
                          <br />
                          Drag and drop the images to customize the gallery
                          order.
                          <div className="flex flex-col !items-center !justify-center text-sm text-gray-500 mt-2">
                            <i className="fa !text-center fa-info-circle mr-2 text-blue-500"></i>{" "}
                            <span>
                              Recommended: 5-20 photos (JPEG, PNG, WEBP, JPG).
                              Images over 1MB will be automatically optimized.
                            </span>
                          </div>
                        </span>
                        <div className="photoUpload-files fl-wrap" />
                        <div className="!text-center pt-[27px]">
                          <span className="custom_image_button">
                            Select and upload
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="photoUpload-files fl-wrap" />
              </div>
              <div
                className="row"
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "flex-start",
                }}
              >
                {imageList.map((image, index) => (
                  <ImageCard
                    key={image.id || index} // Use image.id for key if available, otherwise index
                    image={image}
                    index={index}
                    moveImage={moveImage}
                    onDelete={() => handleDelete(index, image.id, image)}
                    isFeatured={index === 0} // Featured image is always the first one
                    onStarClick={handleStarClick}
                    onDescriptionClick={handleDescriptionClick}
                  />
                ))}
              </div>
            </div>
          );
        }}
      </ImageUploading>
      {errors && <p className="text-red-500 text-sm mt-2">{errors.message}</p>}
      {isImageModalOpen && (
        <ModalImage
          saveDescription={saveDescription}
          closeModal={closeImageModal}
          selectedImage={selectedImage}
          description={description}
          isModalOpen={isImageModalOpen}
          setDescription={setDescription}
        />
      )}
    </div>
  );
};

export default ImageUploadingComponent;
